// Pure, locale-independent display helpers for the station pages. Locale/unit
// formatting (height, distance, time, dates) lives in ../_units (useFormat).

export function locationLine({ city_name, state_name, country_name }) {
  return [city_name, state_name, country_name].filter(Boolean).join(', ')
}

// Current velocity is signed: positive = flood, negative = ebb.
export function direction(t, v) {
  if (v == null || v === 0) return t('slack')
  return v > 0 ? t('flood') : t('ebb')
}

export function dirColor(v) {
  if (v == null || v === 0) return 'gray'
  return v > 0 ? 'teal' : 'orange'
}

// "waxing_gibbous" -> "Waxing Gibbous" (fallback for unknown phase strings).
export function humanizePhase(phase) {
  return phase ? phase.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : ''
}

// Moon phase id -> translation key. third_quarter is an alias of last_quarter.
const PHASE_KEY = {
  new_moon: 'phase_new_moon',
  waxing_crescent: 'phase_waxing_crescent',
  first_quarter: 'phase_first_quarter',
  waxing_gibbous: 'phase_waxing_gibbous',
  full_moon: 'phase_full_moon',
  waning_gibbous: 'phase_waning_gibbous',
  last_quarter: 'phase_last_quarter',
  third_quarter: 'phase_last_quarter',
  waning_crescent: 'phase_waning_crescent',
}
// Localized moon-phase label (pass the component's t). Unknown phases fall back
// to the title-cased English id.
export function moonPhaseLabel(t, phase) {
  const key = PHASE_KEY[phase]
  return key ? t(key) : humanizePhase(phase)
}

const MOON_EMOJI = {
  new_moon: '🌑',
  waxing_crescent: '🌒',
  first_quarter: '🌓',
  waxing_gibbous: '🌔',
  full_moon: '🌕',
  waning_gibbous: '🌖',
  last_quarter: '🌗',
  third_quarter: '🌗',
  waning_crescent: '🌘',
}
export function moonPhaseEmoji(phase) {
  return MOON_EMOJI[phase] || '🌙'
}

// Insights bands (ported from the mobile app's config/insights.js). The band
// drives both a cell background color and a label.
const BAND_COLORS = {
  smaller_than_usual: '#0a405a',
  slower_than_usual: '#0a405a',
  lower_than_usual: '#0a405a',
  typical: '#105f87',
  larger_than_usual: '#5a8a2a',
  faster_than_usual: '#5a8a2a',
  unusually_large: '#ED7D31',
  unusually_fast: '#ED7D31',
  unusually_low: '#ED7D31',
}
export function bandColor(band) {
  return BAND_COLORS[band] || '#105f87'
}

const BAND_KEYS = new Set([
  'smaller_than_usual',
  'typical',
  'larger_than_usual',
  'unusually_large',
  'lower_than_usual',
  'unusually_low',
  'slower_than_usual',
  'faster_than_usual',
  'unusually_fast',
])
export function bandLabel(t, band) {
  return BAND_KEYS.has(band) ? t(`band_${band}`) : ''
}

// Height-specific overrides used only on the Tidal Extremes card.
const BAND_KEYS_HEIGHT = new Set(['smaller_than_usual', 'larger_than_usual', 'unusually_large'])
export function bandLabelHeight(t, band) {
  return BAND_KEYS_HEIGHT.has(band) ? t(`band_${band}_height`) : bandLabel(t, band)
}
